import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ERROR_API_CALL,
  SHIPPING_ADDRESS_UNCONFIRMED,
  WREG_FAILURE_REASONS,
  WREG_REGISTRATION_COMPLETED,
  WREG_REGISTRATION_ENDED,
  WREG_REGISTRATION_FAILED,
} from '@/consts/events';
import { useApiError } from '@/context/ApiErrorContext';
import { useRegistration } from '@/context/RegistrationContext';
import { buildLabsConsent } from '@/utils/labsConsent';
import { apiClient, type ConsentMetadata } from '@/services/api';
import { useAnalytics } from '@/hooks/useAnalytics';
import type { ACCESSAlignmentResultCode } from '@/types/registration';
import type { WregIntakeForm } from '@/components/webreg/screens/wregIntakeFields';
import {
  checkShippingAddress,
  toShippingData,
  type ShippingForm,
} from '@/components/webreg/screens/wregShippingFields';
import {
  isWregExceptionStep,
  type WregExceptionStep,
} from '@/components/webreg/screens/wregExceptions';
import { useWregAlignment, wregAlignIdentity } from '@/components/webreg/useWregAlignment';
import { useWregEligibility } from '@/components/webreg/useWregEligibility';
import { useWregShipping } from '@/components/webreg/useWregShipping';
import {
  promoCodeUnusable,
  signupErrStatus,
  useWregSignup,
} from '@/components/webreg/useWregSignup';
import { useWregNav } from '@/components/webreg/useWregNav';
import { WREG_ENTRY_PATH } from '@/components/webreg/wregRoutes';
import {
  wregAlignEnrolled,
  wregAlignNeedsAttestation,
  wregAlignStep,
} from '@/components/webreg/wregAlignment';
import { wregEligPending, wregEligStep, wregEligSwitch } from '@/components/webreg/wregEligibility';
import {
  wregFormFromContext,
  wregFormToContext,
  wregTracksFromContext,
  wregTracksToContext,
} from '@/components/webreg/wregFlowState';
import type { WregStep } from '@/components/webreg/wregSteps';
import {
  WREG_SCENARIO_ALIGN_CODE,
  WREG_SCENARIO_RESUMABLE,
  wregElig,
  type WregTrackId,
} from '@/components/webreg/wregScenarios';
import { useWregDev } from '@/components/webreg/dev/WregDevContext';

export const WREG_INTAKE_HELD = 'wreg-intake-held';

export const WREG_RETRY_HELD = 'wreg-retry-held';

export const WREG_SHIPPING_HELD = 'wreg-shipping-held';

type RetryStage = 'align' | 'intake' | null;

const ENDED_ENROLLED = 'enrolled';

const ENDED_OUTCOME: Record<WregExceptionStep, string> = {
  'x-notelig': 'not_eligible',
  'x-already': 'already_enrolled',
  'x-control': 'control_group',
  'x-mismatch': 'details_mismatch',
  'x-noqualify': 'no_qualifying_condition',
  'x-error': 'processing_error',
  'x-switchok': ENDED_ENROLLED,
  'x-outreach': 'outreach',
  'x-labspending': 'labs_pending',
  'x-declined': 'declined',
};

export type WregFlowActions = {
  form: WregIntakeForm;
  intakeNotice: string | null;
  retryStage: RetryStage;
  helpOpen: boolean;
  setHelpOpen: (open: boolean) => void;
  tracks: WregTrackId[];
  elig: ReturnType<typeof wregElig>;
  tracksGate: boolean;
  runIntake: (submitted: WregIntakeForm) => Promise<void>;
  afterIntake: () => void;
  runAlign: () => Promise<void>;
  afterAlign: () => void;
  retryFromError: () => Promise<void>;
  afterRetry: () => void;
  saveShipping: (address: ShippingForm) => Promise<void>;
  onScreenerContinue: () => void;
  onScreenerIneligible: () => void;
  onTracksContinue: (ids: WregTrackId[]) => void;
  onTracksNoCondition: () => void;
  onDecline: () => void;
  onSwitchAttest: () => void;
  onLabsYes: () => void;
  onLabsNo: () => void;
  onShippingContinued: () => void;
  onAckToAllSet: () => void;
  reenterDetails: () => void;
  startOver: () => void;
  restartFlow: () => void;
  jumpToEnd: (target: WregStep) => void;
};

const WregFlowContext = createContext<WregFlowActions | undefined>(undefined);

const WregFlowProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dev = useWregDev();
  const { go, replace } = useWregNav();
  const {
    registrationData,
    updateLabs,
    updateShipping,
    updateConsent,
    updateSelectedTracks,
    updateMedicareCoverage,
    updateAccount,
    updateWreg,
    clearRegistrationData,
  } = useRegistration();
  const { submit: submitEligibility } = useWregEligibility();
  const { submit: submitAlignment } = useWregAlignment();
  const { submit: submitSignup, recordConsents } = useWregSignup();
  const { submit: submitShipping } = useWregShipping();
  const { showApiError } = useApiError();
  const { track, trackError, resetIdentity } = useAnalytics();

  const [typedForm, setForm] = useState<WregIntakeForm | null>(null);
  const [intakeNotice, setIntakeNotice] = useState<string | null>(null);
  const [retryStage, setRetryStage] = useState<RetryStage>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const afterIntakeRef = useRef<WregStep>('screener');
  const afterAlignRef = useRef<WregStep>('allset');
  const afterShippingRef = useRef<WregStep>('allset');
  const pendingLabsConsentRef = useRef<ConsentMetadata[]>([]);
  const lastAddressRef = useRef<ShippingForm | null>(null);
  const alignRef = useRef<{ identity: string; step: WregStep } | null>(null);
  const alignCodeRef = useRef<ACCESSAlignmentResultCode | undefined>(undefined);
  const alignSubmissionIdRef = useRef<string | undefined>(undefined);
  const alignErrorRetriedRef = useRef(false);
  // One completion and one ending per registration. Reporting the completion from saveShipping
  // rather than runAlign lost the dedup that runAlign's identity cache used to provide for free,
  // and the address screen is re-enterable: /all-set is pushed rather than replaced, the monitor
  // gate still passes on the way back, and every call below the guard answers a repeat submit
  // without throwing. Deliberately not keyed on the address — an edit changes the shipping
  // identity and does send a second shipment, but it is still one registration finishing once.
  // The ending needs the same guard for the same walk: ANALYTICS.md sells it as firing exactly
  // once per finished flow and uses it as the denominator under the completion, so letting it
  // double while the completion does not would read as a success rate below 1.
  const completedRef = useRef(false);
  const endedRef = useRef(false);

  const form = typedForm ?? wregFormFromContext(registrationData);
  const { wreg } = registrationData;
  const result = wreg.eligCode ?? dev.mockCode;
  const attested = wreg.switchAttested;
  const tracks = wregTracksFromContext(registrationData.selectedTracks);
  // Both codes, matching WREG_GATE's own needsSwitch. CMS can ask for a switch attestation at align
  // time even when eligibility did not flag one, and reading only the eligibility code meant the
  // member went to the switch screen, attested, and then re-aligned on the PLAIN route with no
  // attestation, so CMS asked again: a dead end. Pre-existing on main; it sits on the path RND-38229
  // moved, so it is fixed here.
  const needsSwitch =
    wregEligSwitch(result) || wregAlignNeedsAttestation(wreg.alignCode ?? undefined);
  const elig = wregElig(dev.scenario);

  const exitTo = useCallback(
    (step: WregStep) => {
      if (isWregExceptionStep(step)) {
        updateWreg({ exit: step });
        track({ event: WREG_REGISTRATION_ENDED, properties: { outcome: ENDED_OUTCOME[step] } });
      }
      replace(step);
    },
    [replace, track, updateWreg],
  );


  // RND-37692: the wipe is this action, not a visit to Landing. Landing's mount effect used to
  // call clearRegistrationData() for every arrival, so any gate bounce silently destroyed a
  // half-finished registration; every explicit Start Over now clears here instead.
  const startOver = useCallback(() => {
    clearRegistrationData();
    resetIdentity();
    navigate(WREG_ENTRY_PATH, { replace: true });
  }, [clearRegistrationData, navigate, resetIdentity]);

  const alignStep = useCallback(
    (code: ACCESSAlignmentResultCode | undefined): WregStep => {
      const target = wregAlignStep(code);
      return target === 'switch' && attested ? 'x-error' : target;
    },
    [attested],
  );

  const runAlign = useCallback(async () => {
    if (needsSwitch && !attested) {
      afterAlignRef.current = 'switch';
      return;
    }
    if (dev.scenario === 'align-error' && !attested && !alignErrorRetriedRef.current) {
      alignErrorRetriedRef.current = true;
      afterAlignRef.current = 'x-error';
      setRetryStage('align');
      return;
    }
    if (!dev.live) {
      const mocked = WREG_SCENARIO_ALIGN_CODE[dev.scenario];
      if (mocked !== undefined) {
        alignCodeRef.current = mocked;
        updateWreg({ alignCode: mocked });
        afterAlignRef.current = alignStep(mocked);
        return;
      }
      afterAlignRef.current = 'allset';
      return;
    }
    const identity = wregAlignIdentity(form, needsSwitch);
    const known = alignRef.current;
    if (known?.identity === identity) {
      afterAlignRef.current = known.step;
      return;
    }
    const outcome = await submitAlignment(form, needsSwitch);
    if (!outcome.ok) {
      afterAlignRef.current = 'x-error';
      setRetryStage('align');
      track({
        event: WREG_REGISTRATION_FAILED,
        properties: { reason: WREG_FAILURE_REASONS.CMS_UNAVAILABLE },
      });
      return;
    }
    const target = alignStep(outcome.resultCode);
    alignCodeRef.current = outcome.resultCode;
    alignSubmissionIdRef.current = outcome.submissionId;
    updateWreg({ alignCode: outcome.resultCode ?? null });
    // Success is reported by saveShipping, not here (RND-38794). CMS aligning the member is the
    // step before the account exists, so a completion counted at this point includes everyone whose
    // signup or shipping then failed — the funnel read as finished for a member with no account.
    if (!wregAlignEnrolled(outcome.resultCode)) {
      track({
        event: WREG_REGISTRATION_FAILED,
        properties: {
          reason: WREG_FAILURE_REASONS.ALIGNMENT_REJECTED,
          result_code: outcome.resultCode,
        },
      });
    }
    alignRef.current = { identity, step: target };
    afterAlignRef.current = target;
  }, [
    alignStep,
    attested,
    dev.live,
    dev.scenario,
    form,
    needsSwitch,
    submitAlignment,
    track,
    updateWreg,
  ]);

  const afterAlign = useCallback(() => {
    const next = afterAlignRef.current;
    return next === 'allset' ? go(next) : exitTo(next);
  }, [exitTo, go]);

  const promoCapReached = useCallback(async () => {
    const code = registrationData.promoCodeData?.code;
    if (code === undefined || code === '') return false;
    try {
      const response = await apiClient.promoCode(code);
      return response.capReached === true;
    } catch (err) {
      trackError({
        event: ERROR_API_CALL,
        properties: { service: 'wreg_promo', status: signupErrStatus(err) },
      });
      return promoCodeUnusable(err);
    }
  }, [registrationData.promoCodeData?.code, trackError]);

  // The address CTA owns the whole back half of the flow (RND-38229): validate, align, and only
  // create the member once CMS has aligned them. WebReg_eCKM_PRD.md step 4 and the 2026-09-01
  // approval both put it in that order, so an unaligned member never gets an account.
  const saveShipping = useCallback(
    async (address: ShippingForm) => {
      lastAddressRef.current = address;

      if (dev.live && (await promoCapReached())) {
        track({
          event: WREG_REGISTRATION_FAILED,
          properties: { reason: WREG_FAILURE_REASONS.PROMO_CAP_REACHED },
        });
        showApiError();
        throw new Error(WREG_SHIPPING_HELD);
      }

      const verdict = await checkShippingAddress(address);
      if (!verdict.isValid) track({ event: SHIPPING_ADDRESS_UNCONFIRMED });

      await runAlign();
      const aligned = afterAlignRef.current;
      if (aligned !== 'allset') {
        afterShippingRef.current = aligned;
        return;
      }

      // Scenario walks never reach hello-server, same guard runIntake uses for eligibility.
      if (dev.live) {
        const signup = await submitSignup(form, alignSubmissionIdRef.current);
        if (!signup.ok) {
          track({
            event: WREG_REGISTRATION_FAILED,
            properties: {
              reason:
                signup.reason === 'promo-cap'
                  ? WREG_FAILURE_REASONS.PROMO_CAP_REACHED
                  : WREG_FAILURE_REASONS.SIGNUP_FAILED,
            },
          });
          showApiError();
          throw new Error(WREG_SHIPPING_HELD);
        }

        // The freshly-created user is passed in rather than read from context: setUserData is React
        // state and will not have flushed inside this callback, and useWregShipping answers
        // { ok: true, sent: false } on a missing token, which would report success and ship nothing.
        const outcome = await submitShipping(address, signup.userData);
        if (!outcome.ok) {
          track({
            event: WREG_REGISTRATION_FAILED,
            properties: { reason: WREG_FAILURE_REASONS.CLIENT_ERROR },
          });
          showApiError();
          throw new Error(WREG_SHIPPING_HELD);
        }

        if (pendingLabsConsentRef.current.length > 0) {
          void recordConsents(pendingLabsConsentRef.current);
          pendingLabsConsentRef.current = [];
        }

        // The completion, reported where the registration actually completes: aligned at CMS, the
        // account created, the monitor on its way. Every failure above this line throws or returns,
        // so a member who got no account is counted by WREG_REGISTRATION_FAILED alone. A retry
        // after a failed shipping submit reaches here once, because the throw happened first.
        if (!completedRef.current && wregAlignEnrolled(alignCodeRef.current)) {
          completedRef.current = true;
          track({
            event: WREG_REGISTRATION_COMPLETED,
            properties: { result_code: alignCodeRef.current },
          });
        }
      }

      updateShipping(toShippingData(address));
      afterShippingRef.current =
        alignCodeRef.current === 'aligned-switch-approved' ? 'x-switchok' : 'allset';
    },
    [
      dev.live,
      form,
      promoCapReached,
      recordConsents,
      runAlign,
      showApiError,
      submitShipping,
      submitSignup,
      track,
      updateShipping,
    ],
  );

  const runIntake = useCallback(
    async (submitted: WregIntakeForm) => {
      setForm(submitted);
      const writes = wregFormToContext(submitted);
      updateMedicareCoverage(writes.medicareCoverage);
      updateAccount(writes.account);
      updateWreg(writes.wreg);
      updateConsent(true);
      if (!dev.live) {
        // Both fields, always. updateWreg is a partial merge and wregGates recomputes the step
        // from the slice on every navigation, so writing eligCode alone could leave a stale
        // eligResumable behind and split the decision: routed to x-already while the screener
        // gate stays open. Only reachable from dev/seeded state today, but it is on this path.
        const resumable = WREG_SCENARIO_RESUMABLE[dev.scenario] ?? false;
        updateWreg({ eligCode: result, eligResumable: resumable });
        afterIntakeRef.current = wregEligStep(result, resumable);
        return;
      }
      const outcome = await submitEligibility(submitted);
      if (outcome.ok) {
        updateWreg({ eligCode: outcome.code, eligResumable: outcome.resumable });
        afterIntakeRef.current = wregEligStep(outcome.code, outcome.resumable);
        return;
      }
      if (outcome.reason === 'rejected' || outcome.reason === 'failed') {
        afterIntakeRef.current = 'x-error';
        setRetryStage('intake');
        track({
          event: WREG_REGISTRATION_FAILED,
          properties: {
            reason:
              outcome.reason === 'rejected'
                ? WREG_FAILURE_REASONS.ELIGIBILITY_REJECTED
                : WREG_FAILURE_REASONS.CMS_UNAVAILABLE,
          },
        });
        return;
      }
      if (outcome.reason === 'invalid-dob') {
        setIntakeNotice(t('wreg.intake.dobnotice'));
        track({
          event: WREG_REGISTRATION_FAILED,
          properties: { reason: WREG_FAILURE_REASONS.INVALID_DOB },
        });
      }
      throw new Error(WREG_INTAKE_HELD);
    },
    [
      dev.live,
      result,
      submitEligibility,
      t,
      track,
      updateAccount,
      updateConsent,
      updateMedicareCoverage,
      updateWreg,
    ],
  );

  const afterIntake = useCallback(() => {
    setIntakeNotice(null);
    const next = afterIntakeRef.current;
    if (isWregExceptionStep(next)) return exitTo(next);
    return go(next);
  }, [exitTo, go]);

  const retryFromError = useCallback(async () => {
    if (retryStage === 'intake') {
      await runIntake(form);
      if (afterIntakeRef.current === 'x-error') throw new Error(WREG_RETRY_HELD);
      return;
    }
    const address = lastAddressRef.current;
    if (address === null) {
      await runAlign();
    } else {
      try {
        await saveShipping(address);
      } catch {
        throw new Error(WREG_RETRY_HELD);
      }
    }
    if (afterAlignRef.current === 'x-error') throw new Error(WREG_RETRY_HELD);
  }, [form, retryStage, runAlign, runIntake, saveShipping]);

  const afterRetry = useCallback(() => {
    const next =
      retryStage === 'intake'
        ? afterIntakeRef.current
        : lastAddressRef.current !== null
          ? afterShippingRef.current
          : afterAlignRef.current;
    setRetryStage(null);
    setIntakeNotice(null);
    updateWreg({ exit: isWregExceptionStep(next) ? next : null });
    replace(next);
  }, [replace, retryStage, updateWreg]);

  const onScreenerContinue = useCallback(() => {
    updateSelectedTracks(wregTracksToContext(['eckm']));
    go(needsSwitch ? 'switch' : 'labs');
  }, [go, needsSwitch, updateSelectedTracks]);

  const onTracksContinue = useCallback(
    (ids: WregTrackId[]) => {
      updateSelectedTracks(wregTracksToContext(ids));
      go(needsSwitch ? 'switch' : 'labs');
    },
    [go, needsSwitch, updateSelectedTracks],
  );

  const onSwitchAttest = useCallback(() => {
    updateWreg({ switchAttested: true });
    go('labs');
  }, [go, updateWreg]);

  const onLabsYes = useCallback(() => {
    updateLabs(true);
    pendingLabsConsentRef.current = [buildLabsConsent(true, Date.now())];
    if (dev.scenario === 'labs-missing') return exitTo('x-labspending');
    return go('monitor');
  }, [dev.scenario, exitTo, go, updateLabs]);

  const onLabsNo = useCallback(() => {
    updateLabs(false);
    pendingLabsConsentRef.current = [];
    return exitTo('x-outreach');
  }, [exitTo, updateLabs]);

  const reenterDetails = useCallback(() => {
    setIntakeNotice(t('wreg.x.mismatch.notice'));
    setRetryStage(null);
    updateWreg({ exit: null });
    replace('intake');
  }, [replace, t, updateWreg]);

  const restartFlow = useCallback(() => {
    setHelpOpen(false);
    startOver();
  }, [startOver]);

  const jumpToEnd = useCallback(
    (target: WregStep) => {
      updateWreg({ switchAttested: true });
      exitTo(target);
    },
    [exitTo, updateWreg],
  );

  const value = useMemo<WregFlowActions>(
    () => ({
      form,
      intakeNotice,
      retryStage,
      helpOpen,
      setHelpOpen,
      tracks,
      elig,
      tracksGate: elig.gate || wregEligPending(result),
      runIntake,
      afterIntake,
      runAlign,
      afterAlign,
      retryFromError,
      afterRetry,
      saveShipping,
      onScreenerContinue,
      onScreenerIneligible: () => exitTo('x-notelig'),
      onTracksContinue,
      onTracksNoCondition: () => exitTo('x-noqualify'),
      onDecline: () => exitTo('x-declined'),
      onSwitchAttest,
      onLabsYes,
      onLabsNo,
      onShippingContinued: () => {
        const next = afterShippingRef.current;
        if (next !== 'allset') return exitTo(next);
        if (!endedRef.current) {
          endedRef.current = true;
          track({ event: WREG_REGISTRATION_ENDED, properties: { outcome: ENDED_ENROLLED } });
        }
        return go('allset');
      },
      onAckToAllSet: () => go('allset'),
      reenterDetails,
      startOver,
      restartFlow,
      jumpToEnd,
    }),
    [
      afterAlign,
      afterIntake,
      afterRetry,
      elig,
      exitTo,
      form,
      go,
      helpOpen,
      intakeNotice,
      jumpToEnd,
      onLabsNo,
      onLabsYes,
      result,
      track,
      onScreenerContinue,
      onSwitchAttest,
      onTracksContinue,
      reenterDetails,
      restartFlow,
      retryFromError,
      retryStage,
      runAlign,
      runIntake,
      saveShipping,
      startOver,
      tracks,
    ],
  );

  return <WregFlowContext.Provider value={value}>{children}</WregFlowContext.Provider>;
};

const useWregFlowActions = (): WregFlowActions => {
  const value = useContext(WregFlowContext);
  if (value === undefined) throw new Error('useWregFlowActions must be used inside WregFlowProvider');
  return value;
};

export { WregFlowProvider, useWregFlowActions };
